<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {Icon} from '@iconify/vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
// @ts-ignore
import html2pdf from 'html2pdf.js'
import {getInvoiceByPackingListId} from "@/api/packingList";
import type {Invoice} from "@/types/invoice-types";
import {getOrderProductById} from "@/api/orders";
import {getProductList} from "@/api/products";
import {getCustomerList} from "@/api/customers";
import type {Address} from "@/types/customer-types";

const props = defineProps<{
  visible: boolean,
  onClose: () => void
  packingListId: string,
}>()

const invoiceData = ref<Invoice>()

const fetchInvoiceData = async () => {
  try {
    invoiceData.value = await getInvoiceByPackingListId(props.packingListId)
  } catch (error) {
    console.error('Error fetching invoice:', error)
  }
}

const formatToLongDate = (date: Date | string) => {
  const dateObject = date instanceof Date ? date : new Date(date)
  return dateObject.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).replace(/(\d+)/, (match) => {
    const day = parseInt(match)
    const suffix = ['th', 'st', 'nd', 'rd'][(day > 3 && day < 21) || day % 10 > 3 ? 0 : day % 10]
    return day + suffix
  })
}

watch(() => props.visible, (newValue) => {
  if (newValue) {
    fetchInvoiceData()
  }
})
const onDownloadInvoice = () => {
    const invoiceElement = document.getElementById('invoice')
  if (invoiceElement) {
    html2pdf()
      .set({
        margin: 5,
        filename: 'invoice.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(invoiceElement)
      .save()
  }
}

const orderProducts = ref<any[]>([])

const fetchOrderProducts = async () => {
  if (invoiceData.value?.orderProductIds) {
    const productPromises = invoiceData.value.orderProductIds.map(id =>
        getOrderProductById(id)
    )
    orderProducts.value = await Promise.all(productPromises)
  }
}

watch(() => invoiceData.value, async (newValue) => {
  if (newValue) {
    await fetchOrderProducts()
  }
})

const productsList = ref<any[]>([])
const customerList = ref<any>([])

const getQuality = (id: string) => {
  return productsList.value.find((product: { id: string }) => product.id === id)?.finishedReference
}

const currentCustomer = computed(() => {
  if (!invoiceData.value?.customerId) return null
  return customerList.value.find((customer: { id: string }) => customer.id === invoiceData.value?.customerId) || null
})

const invoiceAddress = computed(() => {
  if (!currentCustomer.value || !currentCustomer.value.addresses || currentCustomer.value.addresses.length === 0) {
    return null;
  }

  return currentCustomer.value.addresses.find((address: Address) => address.addressType === 0) || currentCustomer.value.addresses[0];
})

const calculateSubTotal = computed((): number => {
  return orderProducts.value.reduce((acc, product) => acc + Number(product.pricePerUnitAmount), 0)
})

const calculateVAT = computed((): number => {
  return Number((calculateNetPrice.value * 0.20).toFixed(2))
})

const calculateGrandTotal = computed((): number => {
  return Number((calculateSubTotal.value + calculateVAT.value + calculateNetPrice.value + 30).toFixed(2))
})

const calculateNetPrice = computed((): number => {
  return orderProducts.value.reduce((acc, product) => acc + Number(product.totalLengthValue) * Number(product.pricePerUnitAmount), 0)
})

onMounted(async () => {
  productsList.value = await getProductList()
  customerList.value = await getCustomerList()
})

const deliveryCharges = '£30.00'
const noteContent = `Please note that we accept no responsibility for claim on goods covered by this invoice unless both we and the carriers are notified in writing within 3 days of non-delivery against the appropriate delivery note and immediately on receipt for shortage and pilferage. Title to the above goods remains with Techktnital Fabrics Ltd in the form of a lien and will not be transferred until all goods and services are paid in full.
All goods are supplied subject to our standard terms and conditions printed on the reverse of this document or on our web site: http://www.techknital.com`
</script>

<template>
  <Dialog :visible="visible" modal :draggable="false" :style="{ width: '70rem' }" :closable="false">
    <div class="flex justify-between items-center mb-4">
      <Button
        link
        class="flex items-center gap-x-2 group hover:text-primary-default"
        @click="onClose"
      >
        <Icon icon="material-symbols:arrow-back-rounded" class="text-3xl group-hover:translate-x-2 transition-transform" />
        <span>Back to Packing List</span>
      </Button>
      <div class="flex gap-4">
        <Button label="Download as PDF"  class="p-button-danger" @click="onDownloadInvoice">
          <template #icon>
            <Icon icon="material-symbols:download-2" class="text-xl ml-3 order-1" />
          </template>
        </Button>
      </div>
    </div>

    <h1 class="font-semibold text-xl mb-2">Invoice Page</h1>
    <hr class="mb-4" />
    <div class="p-6 bg-white rounded-lg shadow" id="invoice">
      <!-- Company and Invoice Details -->
      <div class="flex justify-between items-center mb-4">
        <div>
          <img src="@/assets/images/logo.png" alt="Company Logo" class="h-10" />
          <p class="text-gray-600 ml-8">sarah@techknital.com</p>
        </div>
        <div><p class="text-primary-500 text-xl">{{ invoiceData?.invoiceId }}</p></div>
        <div>
          <p class="text-gray-600 text-sm">Unit 8 - 9, Lime Kilns Business Park<br />Lime Kilns Way, Hinckley, LE10 3EL</p>
          <p class="text-sm">Tel: +44 (0)1455 254350<br />Fax: +44 (0)1455 238998</p>
        </div>
      </div>
      <!-- Invoice Details and Billed To -->
      <div class="mt-6 flex flex-row justify-between gap-4 p-4 bg-gray-50 rounded">
        <div>
          <h4 class="font-semibold">Invoice Details</h4>
          <p class="text-sm">Invoice No: {{ invoiceData?.invoiceId }}</p>
          <p class="text-sm">Packing List ID: {{ invoiceData?.packingListId }}</p>
          <p class="text-sm">Issued Date: {{ invoiceData?.issuedDate ? formatToLongDate(invoiceData?.issuedDate) : '-' }}</p>
          <p class="text-sm">Due Date: {{ invoiceData?.dueDate ? formatToLongDate(invoiceData?.dueDate) : '-' }}</p>
        </div>
        <div>
          <h4 class="font-semibold">Billed to</h4>
          <template v-if="currentCustomer">
            <p class="text-sm font-medium">{{ currentCustomer.businessName }}</p>
            <template v-if="invoiceAddress">
              <p class="text-sm" v-if="invoiceAddress.line1">
                {{ invoiceAddress.line1 }}
                <template v-if="invoiceAddress.line2">,<br />{{ invoiceAddress.line2 }}</template>
              </p>
              <p class="text-sm">
                <template v-if="invoiceAddress.city">{{ invoiceAddress.city }}</template>
                <template v-if="invoiceAddress.city && invoiceAddress.postCode">, </template>
                <template v-if="invoiceAddress.postCode">{{ invoiceAddress.postCode }}</template>
                <template v-if="(invoiceAddress.city || invoiceAddress.postCode) && invoiceAddress.country">, </template>
                <template v-if="invoiceAddress.country">{{ invoiceAddress.country }}</template>
              </p>
              <p class="text-sm" v-if="invoiceAddress.region">Region: {{ invoiceAddress.region }}</p>
            </template>
            <p class="text-sm" v-if="currentCustomer.salesContactEmail">Email: {{ currentCustomer.salesContactEmail }}</p>
            <p class="text-sm" v-if="currentCustomer.customerContact">Contact: {{ currentCustomer.customerContact }}</p>
            <p class="text-sm" v-if="currentCustomer.customerAccount">Account: {{ currentCustomer.customerAccount }}</p>
          </template>
          <p class="text-sm" v-else>Customer information not available</p>
        </div>
      </div>

      <!-- Notes Section -->
      <div class="mt-6 p-4 bg-gray-50 rounded">
        <h4 class="font-semibold mb-4">Notes</h4>
        <p class="text-sm">{{ noteContent }}</p>
      </div>

      <!-- Item Details Table -->
      <div class="mt-6 overflow-x-auto">
        <table class="min-w-full text-left border-separate border-spacing-y-2 border border-gray-200 rounded-lg">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2">Quality</th>
              <th class="px-4 py-2">Price/Unit</th>
              <th class="px-4 py-2">Length</th>
              <th class="px-4 py-2">Customer Quality Reference</th>
              <th class="px-4 py-2">Net Price</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in orderProducts" :key="product.id" class="border-b">
              <td class="px-4 py-2">{{ getQuality(product.qualityId) }}</td>
              <td class="px-4 py-2">{{ product.pricePerUnitAmount }}</td>
              <td class="px-4 py-2">{{ product.totalLengthValue }}</td>
              <td class="px-4 py-2">{{ product.customerQualityReference }}</td>
              <td class="px-4 py-2">{{ calculateNetPrice.toFixed(2) }}</td>
            </tr>
            <tr>
              <td colspan="6" class="px-4 py-2 font-semibold">Delivery Charges</td>
              <td class="px-4 py-2">{{ deliveryCharges }}</td>
            </tr>
            <tr>
              <td colspan="6" class="px-4 py-2 font-semibold">VAT (20%)</td>
              <td class="px-4 py-2">£{{ calculateVAT.toFixed(2) }}</td>
            </tr>
            <tr class="bg-gray-200">
              <td colspan="6" class="px-4 py-2 font-semibold">Grand Total</td>
              <td class="px-4 py-2">£{{ calculateGrandTotal.toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer Note Section -->
      <div class="mt-6 p-4 bg-gray-50 rounded">
        <h4 class="font-semibold flex items-center gap-2 mb-3">
          <Icon icon="material-symbols:info" /> Note:
        </h4>
        <p class="text-sm">{{ noteContent }}</p>
      </div>

      <!-- Bank Details -->
      <div class="mt-6 p-4 bg-gray-50 rounded">
        <h4 class="font-semibold">Send all remittances to the above address or bank account as below:</h4>
        <p class="text-sm">Bank Name: Handelsbanken PLC</p>
        <p class="text-sm">Bank Address: Leicester Branch, Two Marlborough Court, Leicester LE7 1AD</p>
        <p class="text-sm">Account No: 18883750</p>
        <p class="text-sm">Sort Code: 40-51-62</p>
        <p class="text-sm">SWIFT: HANDGB22</p>
        <p class="text-sm">IBAN: GB85HAND40516218883750</p>
      </div>
    </div>
  </Dialog>
</template>
